import {useApolloClient, useMutation} from '@apollo/client';
import {some} from '@danwang/option';
import {
  UpdateRawTransactionDocument,
  useAttachmentFeePeriodsQuery,
  useRawTransactionsWithConsumersQuery,
  useSetBenefitOnRawTransactionsMutation,
  useUpdateRawTransactionsMutation,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import Money from 'common/Money';
import * as React from 'react';
import {Link} from 'react-router-dom';
import AccountSelect from 'src/AccountSelect';
import {AmountDisplay} from 'src/AmountDisplay';
import evictAccountRawTransactions from 'src/apolloCache/evictAccountRawTransactions';
import {Button} from 'src/Button';
import {BenefitPicker} from 'src/CreateTransactionForBenefitModal/BenefitPicker';
import DatePicker from 'src/DatePicker/DatePicker';
import InputGroup from 'src/InputGroup';
import AmountInput from 'src/inputs/AmountInput';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import {transactionLineHref} from 'src/RawTransactionListView/useRawRowGestures';
import {TRANSFER_TYPE_LABELS} from 'src/TransferKindSelect';
import {evictFeePeriodQueries} from 'src/UnlinkBenefitTransactionModal';
import type {Option} from '@danwang/option';
import type {
  AccountListItemFragment,
  BasicRawTransactionFragment,
  UnifiedTransactionFragment,
} from '@transactions/graphql';
import type {DateTime} from 'luxon';
import type {SystemProps} from 'src/Modals';

export type EditRawTransactionModalUserProps = {
  rawTransaction: BasicRawTransactionFragment;
  /** Called after a successful save with the latest version of the row. */
  onUpdate?: (updated: BasicRawTransactionFragment) => void;
};

/**
 * Staged benefit edit. 'keep' leaves the current link alone; 'picking'
 * shows the picker; 'link'/'clear' are staged changes applied on save.
 */
type BenefitDraft =
  | {kind: 'keep'}
  | {kind: 'picking'}
  | {kind: 'link'; benefitId: string; benefitName: string}
  | {kind: 'clear'};

/** An uneditable field's current value, with the reason it is locked. */
const ReadOnlyValue = ({
  reason,
  children,
}: {
  reason?: string | null;
  children: React.ReactNode;
}) => (
  <div className="text-muted-foreground flex min-h-9 flex-col justify-center py-1.5 text-sm">
    <span className="truncate">{children}</span>
    {reason ? <span className="text-xs opacity-80">{reason}</span> : null}
  </div>
);

/**
 * A plain edit form for one activity line's own facts: every field is
 * prefilled and directly editable (locked fields show why instead), and Save
 * applies only what changed. The transaction that consumes the line is
 * edited in its own sheet; this form links to it. Multi-row selections go
 * through BulkEditRawTransactionsModal instead, where "leave unchanged" must
 * be explicit per property.
 */
export const EditRawTransactionModal = (
  props: EditRawTransactionModalUserProps & SystemProps,
) => {
  const {rawTransaction, onUpdate, onRequestClose} = props;
  const formId = React.useId();
  const client = useApolloClient();

  // The list rows carry no consumption links, so the form loads the
  // transactions it links to itself. network-only: a stale cached link would
  // point at a transaction that no longer holds this line.
  const {data: consumersData} = useRawTransactionsWithConsumersQuery({
    variables: {ids: [rawTransaction._id]},
    fetchPolicy: 'network-only',
  });

  const targets: UnifiedTransactionFragment[] = React.useMemo(() => {
    const byId = new Map<string, UnifiedTransactionFragment>();
    for (const row of consumersData?.rawTransactionsByIds ?? []) {
      for (const consumer of row.consumers) {
        byId.set(consumer.transaction._id, consumer.transaction);
      }
    }
    return [...byId.values()];
  }, [consumersData]);
  // The network-only fetch above also returns the row's current server
  // state. When a save runs no raw-level mutation, this is what goes back
  // to the caller — the prop row can lag a sync that landed before the
  // modal opened, and the caller writes what we hand it into its list.
  const freshRow: BasicRawTransactionFragment | null =
    consumersData?.rawTransactionsByIds.find(
      (rt) => rt._id === rawTransaction._id,
    ) ?? null;

  // --- Field locks --------------------------------------------------------
  // Sync owns the identity fields of Plaid/Venmo rows — the server rejects
  // updates. Account and benefit stay editable on synced rows.
  const isManual = !rawTransaction.plaidId && !rawTransaction.venmoId;
  const syncedReason = isManual ? null : 'Synced from Plaid/Venmo';
  const isConsumed =
    rawTransaction.remainingAmount !== null &&
    rawTransaction.remainingAmount !== rawTransaction.amount;
  const amountReason =
    syncedReason ??
    (isConsumed
      ? 'Locked: a transaction consumes this row'
      : rawTransaction.reconciliation
        ? 'Locked: this row is reconciled'
        : null);
  // Linking is USD-only by server enforcement; clearing never is. A non-USD
  // row with a legacy link can therefore only clear it — no picker.
  const canLinkBenefit = rawTransaction.currency === 'USD';
  const benefitLockedReason =
    !canLinkBenefit && rawTransaction.benefitId == null
      ? 'Benefits are USD-only'
      : null;

  // --- Draft state --------------------------------------------------------
  const originalAmount = Money.fromServer(rawTransaction);
  const originalDate = parseDate(rawTransaction.date);
  const [amount, setAmount] = React.useState<Option<Money>>(
    some(originalAmount),
  );
  const [date, setDate] = React.useState<DateTime>(originalDate);
  const [description, setDescription] = React.useState(
    rawTransaction.description,
  );
  const [account, setAccount] = React.useState<
    Option<AccountListItemFragment>
  >(some(rawTransaction.account));
  const [benefit, setBenefit] = React.useState<BenefitDraft>({kind: 'keep'});
  // Fetched only once the picker opens: AttachmentFeePeriods selects just
  // what BenefitPicker renders, and most edits never touch the benefit row —
  // the full fee-period query drags unbounded transaction histories along.
  const {
    data: feePeriodsData,
    loading: feePeriodsLoading,
    error: feePeriodsError,
    refetch: refetchFeePeriods,
  } = useAttachmentFeePeriodsQuery({
    skip: benefit.kind !== 'picking',
  });
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  // --- Diffs --------------------------------------------------------------
  // An emptied amount is not "unchanged" — the form shows no value, so a
  // save must not silently keep the original. It blocks Save instead.
  const amountInvalid = amountReason === null && amount.isEmpty;
  const amountChanged =
    amountReason === null &&
    amount.map((m) => !m.equals(originalAmount)).getOrElse(false);
  const dateChanged =
    isManual && date.toISODate() !== originalDate.toISODate();
  const descriptionChanged =
    isManual && description !== rawTransaction.description;
  const accountChanged = account
    .map((a) => a._id !== rawTransaction.account._id)
    .getOrElse(false);
  const benefitChange: string | null | undefined =
    benefit.kind === 'link' && benefit.benefitId !== rawTransaction.benefitId
      ? benefit.benefitId
      : benefit.kind === 'clear' && rawTransaction.benefitId != null
        ? null
        : undefined;

  const hasChanges =
    amountChanged ||
    dateChanged ||
    descriptionChanged ||
    accountChanged ||
    benefitChange !== undefined;

  // --- Save ---------------------------------------------------------------
  const [updateRawTransaction] = useMutation(UpdateRawTransactionDocument);
  const [updateRawTransactions] = useUpdateRawTransactionsMutation();
  const [setBenefitOnRawTransactions] =
    useSetBenefitOnRawTransactionsMutation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges || saving || amountInvalid) return;
    setError(null);
    setSaving(true);

    // The save is not one transaction, so order the calls by failure
    // likelihood: the raw-level write carries the validations the form can't
    // pre-check (cross-currency moves, closed accounts, reconciliation and
    // basis locks), so a failed save normally leaves nothing applied.
    const run = async () => {
      let updated: BasicRawTransactionFragment | null = null;
      if (amountChanged || dateChanged || descriptionChanged || accountChanged) {
        if (isManual) {
          // One call, one write: the resolver validates the whole effective
          // (account, date, amount) combination before persisting anything,
          // so a rejected date can never leave an already-saved amount
          // behind.
          const result = await updateRawTransaction({
            variables: {
              id: rawTransaction._id,
              amount: amountChanged
                ? amount.map((m) => m.amount).getOrElse(undefined)
                : undefined,
              date: dateChanged ? date.toISODate() : undefined,
              description: descriptionChanged ? description : undefined,
              account: accountChanged
                ? account.map((a) => a._id).getOrElse(undefined)
                : undefined,
            },
          });
          updated = result.data?.updateRawTransaction ?? updated;
        } else {
          // A synced row can only change its account here (the other fields
          // are provider-owned and locked above), and the single-row
          // resolver rejects synced rows before looking at fields — the
          // bulk mutation permits the account-only move.
          const result = await updateRawTransactions({
            variables: {
              ids: [rawTransaction._id],
              account: account.map((a) => a._id).getOrElse(undefined),
            },
          });
          updated = result.data?.updateRawTransactions?.[0] ?? updated;
        }
        if (accountChanged) {
          // Moving the row rewrites the normalized entity, but cached
          // allRawTransactions pages for both accounts still hold the old
          // membership — drop them. A moved row may also back a fee period
          // as its charge; the resolver drops the link once accounts
          // mismatch, so cached fee period queries go stale too.
          evictAccountRawTransactions(client.cache, rawTransaction.account._id);
          const nextAccountId = updated?.account._id;
          if (nextAccountId) {
            evictAccountRawTransactions(client.cache, nextAccountId);
          }
          evictFeePeriodQueries(client.cache);
        }
      }
      if (benefitChange !== undefined) {
        const result = await setBenefitOnRawTransactions({
          variables: {ids: [rawTransaction._id], benefit: benefitChange},
        });
        updated = result.data?.setBenefitOnRawTransactions?.[0] ?? updated;
        // Benefit usage totals hang off fee period queries
        evictFeePeriodQueries(client.cache);
      }
      return updated;
    };

    run()
      .then((updated) => {
        onRequestClose();
        // Prefer what a raw-level mutation returned, then the row the
        // consumers fetch brought back (current server state — the prop can
        // be stale when a sync landed before the modal opened), and only
        // then the prop itself.
        onUpdate?.(updated ?? freshRow ?? rawTransaction);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to save changes.');
      })
      .finally(() => setSaving(false));
  };

  // --- Rendering ----------------------------------------------------------
  const stagedAccountId = account
    .map((a) => a._id)
    .getOrElse(rawTransaction.account._id);

  const benefitControls = (() => {
    if (benefitLockedReason !== null) {
      return (
        <ReadOnlyValue reason={benefitLockedReason}>
          {rawTransaction.benefit?.name ?? 'None'}
        </ReadOnlyValue>
      );
    }
    switch (benefit.kind) {
      case 'keep':
        return (
          <div className="flex min-h-9 flex-wrap items-center gap-2 py-1.5 text-sm">
            <span
              className={
                rawTransaction.benefit ? undefined : 'text-muted-foreground'
              }
            >
              {rawTransaction.benefit?.name ?? 'None'}
            </span>
            {canLinkBenefit && (
              <Button
                variant="outline"
                className="h-7 px-2.5 text-xs"
                onClick={() => setBenefit({kind: 'picking'})}
              >
                Change
              </Button>
            )}
            {rawTransaction.benefitId != null && (
              <Button
                variant="outline"
                className="h-7 px-2.5 text-xs"
                onClick={() => setBenefit({kind: 'clear'})}
              >
                Clear
              </Button>
            )}
          </div>
        );
      case 'picking':
        return (
          <div className="flex flex-col gap-2 pt-1.5">
            {/* An empty picker means "no benefits", so the pending and
                failed fetches must not render as one. */}
            {feePeriodsLoading ? (
              <div className="text-muted-foreground py-1.5 text-sm">
                Loading benefits…
              </div>
            ) : feePeriodsError ? (
              <div className="flex items-center gap-2 py-1.5 text-sm">
                <span className="text-destructive">
                  Failed to load benefits.
                </span>
                <Button
                  variant="outline"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => void refetchFeePeriods()}
                >
                  Retry
                </Button>
              </div>
            ) : (
            <div className="max-h-64 overflow-y-auto">
              <BenefitPicker
                feePeriods={feePeriodsData?.allFeePeriods ?? []}
                transactionDate={date.toSeconds()}
                accountId={stagedAccountId}
                // No lockAccount: linking one row across accounts is the
                // same escape hatch LinkBenefitModal offers behind "Show
                // all benefits" — only a multi-row bulk link must stay on
                // one account.
                onSelect={(selected) =>
                  setBenefit({
                    kind: 'link',
                    benefitId: selected._id,
                    benefitName: selected.name,
                  })
                }
              />
            </div>
            )}
            <Button
              variant="outline"
              className="h-7 self-start px-2.5 text-xs"
              onClick={() => setBenefit({kind: 'keep'})}
            >
              Cancel
            </Button>
          </div>
        );
      case 'link':
        return (
          <div className="flex min-h-9 flex-wrap items-center gap-2 py-1.5 text-sm">
            <span>
              Link to <span className="font-medium">{benefit.benefitName}</span>
            </span>
            <Button
              variant="outline"
              className="h-7 px-2.5 text-xs"
              onClick={() => setBenefit({kind: 'picking'})}
            >
              Change
            </Button>
            <Button
              variant="outline"
              className="h-7 px-2.5 text-xs"
              onClick={() => setBenefit({kind: 'keep'})}
            >
              Undo
            </Button>
          </div>
        );
      case 'clear':
        return (
          <div className="flex min-h-9 flex-wrap items-center gap-2 py-1.5 text-sm">
            <span>The benefit link will be removed</span>
            <Button
              variant="outline"
              className="h-7 px-2.5 text-xs"
              onClick={() => setBenefit({kind: 'keep'})}
            >
              Undo
            </Button>
          </div>
        );
    }
  })();

  return (
    <Modal
      onRequestClose={onRequestClose}
      size="lg"
      // A bottom sheet on a phone, as the transaction sheets are.
      className="top-auto bottom-0 h-auto max-h-[92dvh] rounded-b-none sm:rounded-b-lg"
      header="Edit activity"
      footer={
        <>
          <Button variant="outline" disabled={saving} onClick={onRequestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!hasChanges || saving || amountInvalid}
            form={formId}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-2">
        <InputGroup label="Amount">
          {amountReason === null ? (
            <div className="flex flex-col gap-1">
              <AmountInput
                value={amount}
                onChange={setAmount}
                textAlign="left"
                currency={rawTransaction.currency}
              />
              {amountInvalid && (
                <span className="text-destructive text-xs">
                  Enter an amount.
                </span>
              )}
            </div>
          ) : (
            <ReadOnlyValue reason={amountReason}>
              <AmountDisplay variant="plain" align="left" amount={originalAmount} />
            </ReadOnlyValue>
          )}
        </InputGroup>
        <InputGroup label="Description">
          {isManual ? (
            <TextInput
              value={description}
              onChange={setDescription}
              placeholder="Description"
            />
          ) : (
            <ReadOnlyValue reason={syncedReason}>
              {rawTransaction.description || (
                <span className="italic">Empty</span>
              )}
            </ReadOnlyValue>
          )}
        </InputGroup>
        <InputGroup label="Date">
          {isManual ? (
            <DatePicker selectedDate={date} onChange={setDate} />
          ) : (
            <ReadOnlyValue reason={syncedReason}>
              {rawTransaction.date}
            </ReadOnlyValue>
          )}
        </InputGroup>
        <InputGroup label="Account">
          <AccountSelect
            selectedAccount={account}
            onSelectAccount={(next) => {
              setAccount(next);
              // A staged link was picked against the previous account;
              // changing it means re-picking, not silently carrying over.
              setBenefit((prev) =>
                prev.kind === 'link' ? {kind: 'picking'} : prev,
              );
            }}
            currency={rawTransaction.currency}
          />
        </InputGroup>
        <InputGroup label="Benefit">{benefitControls}</InputGroup>

        {targets.length > 0 && (
          <InputGroup label={targets.length === 1 ? 'Transaction' : 'Transactions'}>
            <div className="flex min-h-9 flex-col justify-center gap-1 py-1.5 text-sm">
              {targets.map((t) => (
                <Link
                  key={t._id}
                  to={transactionLineHref(t._id, rawTransaction._id)}
                  onClick={onRequestClose}
                  className="truncate text-blue-600 hover:underline dark:text-blue-400"
                >
                  {t.payee ||
                    (t.transferType ? TRANSFER_TYPE_LABELS[t.transferType] : 'Transfer')}
                </Link>
              ))}
            </div>
          </InputGroup>
        )}

        {error && <p className="text-destructive text-sm">{error}</p>}
      </form>
    </Modal>
  );
};
